/**
 * UserManagement Component
 * Admin interface for managing allowed DataEntry users
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  fetchAllowedUsers,
  addAllowedUser,
  updateAllowedUser,
  deleteAllowedUser,
  type AllowedUser,
  type NewUser,
  type UserUpdates,
} from '../../API/Admin/AdminAPI';
import styles from './UserManagement.module.css';

type ModalMode = 'add' | 'edit' | 'delete' | null;

const UserManagement: React.FC = () => {
  const navigate = useNavigate();
  const { isAdmin, isLoading: authLoading, user } = useAuth();

  // State
  const [users, setUsers] = useState<AllowedUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Modal state
  const [modalMode, setModalMode] = useState<ModalMode>(null);
  const [selectedUser, setSelectedUser] = useState<AllowedUser | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form state
  const [formEmail, setFormEmail] = useState('');
  const [formName, setFormName] = useState('');
  const [formRole, setFormRole] = useState<'admin' | 'editor' | 'viewer'>('viewer');

  // Check admin access
  useEffect(() => {
    if (!authLoading && !isAdmin) {
      navigate('/');
    }
  }, [authLoading, isAdmin, navigate]);

  // Load users on mount
  useEffect(() => {
    if (isAdmin) {
      loadUsers();
    }
  }, [isAdmin]);

  const loadUsers = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const fetchedUsers = await fetchAllowedUsers();
      setUsers(fetchedUsers);
    } catch (err: any) {
      console.error('Failed to load users:', err);
      setError(err.message || 'Failed to load users');
    } finally {
      setIsLoading(false);
    }
  };

  // Filter users based on search
  const filteredUsers = useMemo(() => {
    if (!searchQuery) return users;
    const query = searchQuery.toLowerCase();
    return users.filter(
      (user) =>
        user.email.toLowerCase().includes(query) ||
        user.name.toLowerCase().includes(query) ||
        user.role.toLowerCase().includes(query)
    );
  }, [users, searchQuery]);

  // Modal handlers
  const openAddModal = () => {
    setFormEmail('');
    setFormName('');
    setFormRole('viewer');
    setSelectedUser(null);
    setModalMode('add');
  };

  const openEditModal = (userToEdit: AllowedUser) => {
    setFormEmail(userToEdit.email);
    setFormName(userToEdit.name);
    setFormRole(userToEdit.role);
    setSelectedUser(userToEdit);
    setModalMode('edit');
  };

  const openDeleteModal = (userToDelete: AllowedUser) => {
    setSelectedUser(userToDelete);
    setModalMode('delete');
  };

  const closeModal = () => {
    setModalMode(null);
    setSelectedUser(null);
    setFormEmail('');
    setFormName('');
    setFormRole('viewer');
  };

  // Form submission handlers
  const handleAddUser = async () => {
    if (!formEmail) {
      setError('Email is required');
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);
      const newUser: NewUser = {
        email: formEmail,
        name: formName,
        role: formRole,
      };
      await addAllowedUser(newUser);
      await loadUsers();
      closeModal();
    } catch (err: any) {
      console.error('Failed to add user:', err);
      setError(err.response?.data?.error || err.message || 'Failed to add user');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateUser = async () => {
    if (!selectedUser) return;

    try {
      setIsSubmitting(true);
      setError(null);
      const updates: UserUpdates = {};

      if (formName !== selectedUser.name) {
        updates.name = formName;
      }
      if (formRole !== selectedUser.role) {
        updates.role = formRole;
      }

      if (Object.keys(updates).length === 0) {
        closeModal();
        return;
      }

      await updateAllowedUser(selectedUser.email, updates);
      await loadUsers();
      closeModal();
    } catch (err: any) {
      console.error('Failed to update user:', err);
      setError(err.response?.data?.error || err.message || 'Failed to update user');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;

    try {
      setIsSubmitting(true);
      setError(null);
      await deleteAllowedUser(selectedUser.email);
      await loadUsers();
      closeModal();
    } catch (err: any) {
      console.error('Failed to delete user:', err);
      setError(err.response?.data?.error || err.message || 'Failed to delete user');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleToggleEnabled = async (userToToggle: AllowedUser) => {
    try {
      setError(null);
      await updateAllowedUser(userToToggle.email, { enabled: !userToToggle.enabled });
      await loadUsers();
    } catch (err: any) {
      console.error('Failed to toggle user status:', err);
      setError(err.response?.data?.error || err.message || 'Failed to update user');
    }
  };

  // Render role badge
  const getRoleBadgeClass = (role: string) => {
    switch (role) {
      case 'admin':
        return styles.roleAdmin;
      case 'editor':
        return styles.roleEditor;
      default:
        return styles.roleViewer;
    }
  };

  // Format date
  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  if (authLoading) {
    return <div className={styles.loading}>Loading...</div>;
  }

  if (!isAdmin) {
    return null; // Will redirect
  }

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>User Management</h1>
      <p className={styles.subtitle}>Manage allowed users for the DataEntry application</p>

      {error && <div className={styles.error}>{error}</div>}

      <div className={styles.controls}>
        <div className={styles.searchSection}>
          <input
            type="text"
            className={styles.searchInput}
            placeholder="Search by name, email, or role..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button
            className={styles.refreshButton}
            onClick={loadUsers}
            disabled={isLoading}
          >
            {isLoading ? 'Loading...' : 'Refresh'}
          </button>
          <button className={styles.addButton} onClick={openAddModal}>
            + Add User
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className={styles.loading}>Loading users...</div>
      ) : filteredUsers.length === 0 ? (
        <div className={styles.noResults}>
          {searchQuery ? 'No users match your search' : 'No users found'}
        </div>
      ) : (
        <div className={styles.tableContainer}>
          <table className={styles.usersTable}>
            <thead>
              <tr>
                <th>User</th>
                <th>Role</th>
                <th>Status</th>
                <th>Added</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((userItem) => (
                <tr key={userItem.email}>
                  <td>
                    <div className={styles.userName}>{userItem.name || '(No name)'}</div>
                    <div className={styles.userEmail}>{userItem.email}</div>
                  </td>
                  <td>
                    <span className={`${styles.roleBadge} ${getRoleBadgeClass(userItem.role)}`}>
                      {userItem.role}
                    </span>
                  </td>
                  <td>
                    <span
                      className={`${styles.statusBadge} ${
                        userItem.enabled ? styles.statusEnabled : styles.statusDisabled
                      }`}
                    >
                      {userItem.enabled ? 'Enabled' : 'Disabled'}
                    </span>
                  </td>
                  <td>
                    <div className={styles.metadata}>
                      <div>{formatDate(userItem.addedAt)}</div>
                      {userItem.addedBy && <div>by {userItem.addedBy}</div>}
                    </div>
                  </td>
                  <td>
                    <div className={styles.actions}>
                      <button
                        className={`${styles.actionButton} ${styles.toggleButton}`}
                        onClick={() => handleToggleEnabled(userItem)}
                        disabled={userItem.email === user?.email}
                        title={userItem.email === user?.email ? "You can't disable yourself" : ''}
                      >
                        {userItem.enabled ? 'Disable' : 'Enable'}
                      </button>
                      <button
                        className={`${styles.actionButton} ${styles.editButton}`}
                        onClick={() => openEditModal(userItem)}
                      >
                        Edit
                      </button>
                      <button
                        className={`${styles.actionButton} ${styles.deleteButton}`}
                        onClick={() => openDeleteModal(userItem)}
                        disabled={userItem.email === user?.email}
                        title={userItem.email === user?.email ? "You can't delete yourself" : ''}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add/Edit Modal */}
      {(modalMode === 'add' || modalMode === 'edit') && (
        <div className={styles.modalOverlay} onClick={closeModal}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h2 className={styles.modalTitle}>
              {modalMode === 'add' ? 'Add New User' : 'Edit User'}
            </h2>

            {error && <div className={styles.error}>{error}</div>}

            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Email</label>
              <input
                type="email"
                className={styles.formInput}
                value={formEmail}
                onChange={(e) => setFormEmail(e.target.value)}
                disabled={modalMode === 'edit'}
                placeholder="user@example.com"
              />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Name</label>
              <input
                type="text"
                className={styles.formInput}
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                placeholder="User Name"
              />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Role</label>
              <select
                className={styles.formSelect}
                value={formRole}
                onChange={(e) => setFormRole(e.target.value as 'admin' | 'editor' | 'viewer')}
                disabled={modalMode === 'edit' && selectedUser?.email === user?.email}
              >
                <option value="viewer">Viewer (Read only)</option>
                <option value="editor">Editor (Read + Write)</option>
                <option value="admin">Admin (Full access)</option>
              </select>
              {modalMode === 'edit' && selectedUser?.email === user?.email && (
                <p style={{ color: '#718096', fontSize: '0.85rem', marginTop: '0.5rem' }}>
                  You cannot change your own role
                </p>
              )}
            </div>

            <div className={styles.modalActions}>
              <button className={styles.cancelButton} onClick={closeModal}>
                Cancel
              </button>
              <button
                className={styles.submitButton}
                onClick={modalMode === 'add' ? handleAddUser : handleUpdateUser}
                disabled={isSubmitting || (modalMode === 'add' && !formEmail)}
              >
                {isSubmitting ? 'Saving...' : modalMode === 'add' ? 'Add User' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {modalMode === 'delete' && selectedUser && (
        <div className={styles.modalOverlay} onClick={closeModal}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h2 className={styles.modalTitle}>Delete User</h2>

            <div className={styles.deleteWarning}>
              <p>
                <strong>Are you sure you want to delete this user?</strong>
                This will permanently remove <strong>{selectedUser.email}</strong> from the
                allowed users list. They will no longer be able to access the DataEntry
                application.
              </p>
            </div>

            {error && <div className={styles.error}>{error}</div>}

            <div className={styles.modalActions}>
              <button className={styles.cancelButton} onClick={closeModal}>
                Cancel
              </button>
              <button
                className={`${styles.submitButton} ${styles.deleteSubmitButton}`}
                onClick={handleDeleteUser}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Deleting...' : 'Delete User'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
